import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import PageHeader from '../components/PageHeader';
import { Users, FileText, TrendingUp, PoundSterling, CalendarClock, AlertTriangle, Sparkles, Receipt, CreditCard, ArrowUp, ArrowDown, Minus, Target } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { useAuth } from '../contexts/AuthContext';

const fmt   = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);
const fmt2  = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(n || 0);

const FREQ_LABEL = {
    monthly: 'Monthly', quarterly: 'Quarterly', annually: 'Annual', annual: 'Annual',
    annual_upfront: 'Annual (upfront)', one_off: 'One-off',
};
const FREQ_TONE = {
    monthly: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    quarterly: 'bg-sky-50 text-sky-700 border-sky-200',
    annually: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    annual: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    annual_upfront: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

const STATUS_TONE = {
    sent:      'bg-indigo-50 text-indigo-700 border-indigo-200',
    part_paid: 'bg-amber-50 text-amber-800 border-amber-200',
    overdue:   'bg-red-50 text-red-700 border-red-200',
};

function Delta({ current, previous }) {
    if (!previous) return null;
    const diff = current - previous;
    const pct  = Math.round(Math.abs(diff / previous) * 100);
    if (Math.abs(diff) < 0.5) return <span className="text-[11px] text-[color:var(--bm-text-subtle)] flex items-center gap-0.5"><Minus size={10} /> Flat</span>;
    const up = diff > 0;
    return (
        <span className={`text-[11px] flex items-center gap-0.5 font-medium ${up ? 'text-emerald-600' : 'text-red-500'}`}>
            {up ? <ArrowUp size={10} /> : <ArrowDown size={10} />} {pct}% vs last mo
        </span>
    );
}

function MrrCard({ revenue, navigate }) {
    const rows = revenue.mrr_breakdown || [];
    // Split by how the client actually pays, to confirm guaranteed income
    const monthlyBilled = rows.filter(r => (r.payment_frequency || 'monthly') === 'monthly');
    const otherBilled   = rows.filter(r => (r.payment_frequency || 'monthly') !== 'monthly');
    const sumMonthly = (arr) => arr.reduce((a, r) => a + (r.monthly_fee || 0), 0);

    return (
        <HoverCard openDelay={80} closeDelay={60}>
            <HoverCardTrigger asChild>
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5 cursor-help relative" data-testid="kpi-mrr">
                    <div className="flex items-start justify-between">
                        <span className="text-[11px] uppercase tracking-[0.1em] text-[color:var(--bm-text-subtle)] font-semibold">MRR</span>
                        <TrendingUp size={17} className="text-[color:var(--bm-accent-600)]" />
                    </div>
                    <div className="font-display text-[26px] font-semibold text-[color:var(--bm-text)] mt-2 leading-none">{fmt(revenue.mrr)}</div>
                    <div className="flex items-center justify-between mt-1.5 gap-2">
                        <div className="text-[11.5px] text-[color:var(--bm-text-muted)]">ARR {fmt(revenue.arr)}</div>
                        <span className="text-[10.5px] text-[color:var(--bm-text-subtle)] flex items-center gap-1">
                            {rows.length} {rows.length === 1 ? 'contract' : 'contracts'} · hover
                        </span>
                    </div>
                </div>
            </HoverCardTrigger>
            <HoverCardContent align="start" sideOffset={8} className="w-[440px] p-0 overflow-hidden" data-testid="mrr-breakdown-popover">
                <div className="px-4 py-3 border-b border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)]">
                    <div className="text-[12.5px] font-semibold text-[color:var(--bm-text)]">Recurring revenue breakdown</div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                        <div className="rounded-md bg-white border border-[color:var(--bm-border)] px-2.5 py-1.5">
                            <div className="text-[10px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">Guaranteed / month</div>
                            <div className="font-display text-[16px] font-semibold text-[color:var(--bm-accent-600)]" data-testid="mrr-total-monthly">{fmt2(revenue.mrr)}</div>
                        </div>
                        <div className="rounded-md bg-white border border-[color:var(--bm-border)] px-2.5 py-1.5">
                            <div className="text-[10px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">Guaranteed / year</div>
                            <div className="font-display text-[16px] font-semibold text-[color:var(--bm-text)]" data-testid="mrr-total-annual">{fmt2(revenue.arr)}</div>
                        </div>
                    </div>
                    {otherBilled.length > 0 && (
                        <div className="mt-2 flex items-center gap-3 text-[10.5px] text-[color:var(--bm-text-muted)]">
                            <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1" />Billed monthly: <b className="text-[color:var(--bm-text)]">{fmt(sumMonthly(monthlyBilled))}/mo</b></span>
                            <span><span className="inline-block w-2 h-2 rounded-full bg-indigo-500 mr-1" />Billed annually: <b className="text-[color:var(--bm-text)]">{fmt(sumMonthly(otherBilled))}/mo</b></span>
                        </div>
                    )}
                </div>

                {rows.length === 0 ? (
                    <div className="px-4 py-6 text-center text-[12.5px] text-[color:var(--bm-text-muted)]">No active contracts contributing to MRR yet.</div>
                ) : (
                    <div className="max-h-[300px] overflow-y-auto">
                        <table className="w-full text-[12px]">
                            <thead className="text-[color:var(--bm-text-subtle)] sticky top-0 bg-white border-b border-[color:var(--bm-border)]">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Client</th>
                                    <th className="text-left px-1 py-2 font-medium">Billing</th>
                                    <th className="text-right px-2 py-2 font-medium">/ month</th>
                                    <th className="text-right px-3 py-2 font-medium">/ year</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.contract_id}
                                        onClick={() => navigate(`/contracts/${r.contract_id}`)}
                                        className="border-b border-[color:var(--bm-border)] last:border-0 hover:bg-[color:var(--bm-bg-mute)] cursor-pointer"
                                        data-testid={`mrr-row-${r.contract_id}`}>
                                        <td className="px-3 py-2 font-medium text-[color:var(--bm-text)] truncate max-w-[130px]">{r.client_name}</td>
                                        <td className="px-1 py-2">
                                            <span className={`bm-pill border text-[10px] ${FREQ_TONE[r.payment_frequency] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                                                {FREQ_LABEL[r.payment_frequency] || r.payment_frequency}
                                            </span>
                                        </td>
                                        <td className="px-2 py-2 text-right text-[color:var(--bm-text)] whitespace-nowrap">{fmt2(r.monthly_fee)}</td>
                                        <td className="px-3 py-2 text-right text-[color:var(--bm-text-muted)] whitespace-nowrap">{fmt2(r.annual_fee)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-[color:var(--bm-bg-mute)] font-semibold">
                                    <td className="px-3 py-2 text-[color:var(--bm-text)]" colSpan={2}>Total ({rows.length})</td>
                                    <td className="px-2 py-2 text-right text-[color:var(--bm-accent-600)] whitespace-nowrap">{fmt2(revenue.mrr)}</td>
                                    <td className="px-3 py-2 text-right text-[color:var(--bm-text)] whitespace-nowrap">{fmt2(revenue.arr)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
                <div className="px-4 py-2 text-[10.5px] text-[color:var(--bm-text-subtle)] border-t border-[color:var(--bm-border)]">
                    From active &amp; due-for-renewal contracts. Click a row to open the contract.
                </div>
            </HoverCardContent>
        </HoverCard>
    );
}

function Kpi({ label, value, sub, icon: Icon, tint = 'primary', delta, testId }) {
    const tints = {
        primary: 'text-[color:var(--bm-primary)]',
        accent:  'text-[color:var(--bm-accent-600)]',
        warn:    'text-amber-600',
        danger:  'text-red-600',
        green:   'text-emerald-600',
    };
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5" data-testid={testId}>
            <div className="flex items-start justify-between">
                <span className="text-[11px] uppercase tracking-[0.1em] text-[color:var(--bm-text-subtle)] font-semibold">{label}</span>
                {Icon && <Icon size={17} className={tints[tint]} />}
            </div>
            <div className="font-display text-[26px] font-semibold text-[color:var(--bm-text)] mt-2 leading-none">{value}</div>
            <div className="flex items-center justify-between mt-1.5 gap-2">
                {sub && <div className="text-[11.5px] text-[color:var(--bm-text-muted)]">{sub}</div>}
                {delta}
            </div>
        </div>
    );
}

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [leadgen, setLeadgen] = useState(null);
    const [err, setErr] = useState(null);
    const navigate = useNavigate();
    const { can } = useAuth();

    useEffect(() => {
        api.get('/dashboard/summary').then(r => setData(r.data)).catch(e => setErr(e.message));
        api.get('/leadgen/dashboard/stats').then(r => setLeadgen(r.data)).catch(() => { /* non-fatal */ });
    }, []);

    if (err)   return <div className="p-8 text-red-600">{err}</div>;
    if (!data) return <div className="p-8 text-[color:var(--bm-text-muted)]">Loading dashboard…</div>;

    const { clients, proposals, revenue, renewals, finance, recent_activity } = data;
    const collectedDelta = <Delta current={revenue.collected_this_month} previous={revenue.collected_last_month} />;

    return (
        <div data-testid="dashboard-page" className="bm-fade-in">
            <PageHeader
                title="Dashboard"
                description="Live overview of clients, proposals, revenue and renewals."
                actions={
                    can('crm', 'create') && (
                        <button onClick={() => navigate('/clients?new=1')}
                            className="h-9 px-4 rounded-md text-white text-[13px] font-medium shadow-sm"
                            style={{ background: 'var(--bm-primary)' }} data-testid="dashboard-new-client-btn">
                            + New Client
                        </button>
                    )
                }
            />

            <div className="px-8 py-7 space-y-7">
                {/* ── KPI row ── */}
                <div>
                    <div className="text-[11px] uppercase tracking-[0.1em] text-[color:var(--bm-text-subtle)] font-semibold mb-3">Revenue</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <MrrCard revenue={revenue} navigate={navigate} />
                        <Kpi label="Collected this month" value={fmt(revenue.collected_this_month)}
                             sub={`${fmt(revenue.invoiced_this_month)} invoiced`}
                             icon={PoundSterling} tint="green"
                             delta={collectedDelta} testId="kpi-collected" />
                        <Kpi label="Outstanding" value={fmt(finance.outstanding)}
                             sub={finance.overdue > 0 ? `${fmt(finance.overdue)} overdue` : 'None overdue'}
                             icon={AlertTriangle} tint={finance.overdue > 0 ? 'danger' : 'primary'} testId="kpi-outstanding" />
                        <Kpi label="Revenue at risk" value={fmt(revenue.renewal_mrr_at_risk)}
                             sub="MRR renewing in 90 days" icon={CalendarClock} tint={revenue.renewal_mrr_at_risk > 0 ? 'warn' : 'primary'} testId="kpi-risk" />
                    </div>
                </div>

                {/* ── Finance: Invoices due soon + Recent payments ── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* Invoices due soon */}
                    <div className="lg:col-span-2 bg-white rounded-xl border border-[color:var(--bm-border)] p-6" data-testid="invoices-due-soon-panel">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2 text-[13.5px] font-semibold text-[color:var(--bm-text)]">
                                <Receipt size={16} className="text-[color:var(--bm-primary)]" /> Invoices due soon
                            </div>
                            <button onClick={() => navigate('/invoices?status=sent')} className="text-[12px] text-[color:var(--bm-primary)] hover:underline">View all</button>
                        </div>
                        {finance.invoices_due_soon.length === 0 ? (
                            <div className="text-[13px] text-[color:var(--bm-text-muted)] py-5 text-center">No invoices due in the next 14 days.</div>
                        ) : (
                            <table className="w-full text-[12.5px]">
                                <thead className="text-[color:var(--bm-text-muted)] border-b border-[color:var(--bm-border)]">
                                    <tr>
                                        <th className="text-left py-2 font-medium">Invoice</th>
                                        <th className="text-left py-2 font-medium">Client</th>
                                        <th className="text-left py-2 font-medium">Status</th>
                                        <th className="text-left py-2 font-medium">Due</th>
                                        <th className="text-right py-2 font-medium">Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {finance.invoices_due_soon.map(inv => (
                                        <tr key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)}
                                            className="border-b border-[color:var(--bm-border)] last:border-0 hover:bg-[color:var(--bm-bg-mute)] cursor-pointer"
                                            data-testid={`due-soon-${inv.id}`}>
                                            <td className="py-2.5 font-medium text-[color:var(--bm-text)]">{inv.invoice_number}</td>
                                            <td className="py-2.5 text-[color:var(--bm-text-muted)]">{inv.client_name}</td>
                                            <td className="py-2.5">
                                                <span className={`bm-pill border text-[11px] ${STATUS_TONE[inv.status] || ''}`}>{inv.status}</span>
                                            </td>
                                            <td className="py-2.5 text-[color:var(--bm-text-muted)]">{inv.due_date}</td>
                                            <td className="py-2.5 text-right font-semibold text-[color:var(--bm-text)]">{fmt2(inv.balance_due)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Recent payments */}
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6" data-testid="recent-payments-panel">
                        <div className="flex items-center gap-2 text-[13.5px] font-semibold text-[color:var(--bm-text)] mb-4">
                            <CreditCard size={16} className="text-[color:var(--bm-accent-600)]" /> Recent payments
                        </div>
                        {finance.recent_payments.length === 0 ? (
                            <div className="text-[13px] text-[color:var(--bm-text-muted)] py-5 text-center">No payments recorded yet.</div>
                        ) : (
                            <div className="space-y-2.5">
                                {finance.recent_payments.slice(0, 7).map(p => (
                                    <div key={p.id} className="flex items-center justify-between text-[12.5px] py-1.5 border-b border-[color:var(--bm-border)] last:border-0" data-testid={`recent-pay-${p.id}`}>
                                        <div className="min-w-0">
                                            <div className="text-[color:var(--bm-text)] font-medium truncate">{p.method || 'Payment'}</div>
                                            <div className="text-[11.5px] text-[color:var(--bm-text-subtle)]">{p.payment_date}</div>
                                        </div>
                                        <div className="text-[color:var(--bm-accent-600)] font-semibold shrink-0 ml-3">{fmt2(p.amount)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Clients + Proposals ── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <div className="lg:col-span-2 bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-2 text-[13.5px] font-semibold text-[color:var(--bm-text)]">
                                <Users size={16} className="text-[color:var(--bm-primary)]" /> Clients by status
                            </div>
                            <div className="text-[12px] text-[color:var(--bm-text-muted)]">
                                <span className="font-semibold text-[color:var(--bm-text)]">{clients.total}</span> total ·{' '}
                                <span className="text-[color:var(--bm-accent-600)] font-semibold">{clients.active}</span> active
                            </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {['lead','discovery','proposal_draft','proposal_sent','active','paused','lost','archived'].map(s => (
                                <div key={s} onClick={() => navigate(`/clients?status=${s}`)}
                                    className="bg-[color:var(--bm-bg-mute)] rounded-lg px-3 py-2.5 border border-transparent hover:border-[color:var(--bm-border)] transition cursor-pointer">
                                    <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-medium">{s.replace(/_/g, ' ')}</div>
                                    <div className="font-display text-[19px] font-semibold text-[color:var(--bm-text)] mt-0.5">{clients.by_status[s] || 0}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
                        <div className="flex items-center gap-2 text-[13.5px] font-semibold text-[color:var(--bm-text)] mb-5">
                            <FileText size={16} className="text-[color:var(--bm-primary)]" /> Proposals
                        </div>
                        <div className="space-y-3 mb-5">
                            {[
                                { label: 'Open',   val: proposals.open,  cls: 'text-[color:var(--bm-text)]' },
                                { label: 'Won',    val: proposals.won,   cls: 'text-emerald-600' },
                                { label: 'Lost',   val: proposals.lost,  cls: 'text-red-500' },
                            ].map(r => (
                                <div key={r.label} className="flex items-center justify-between text-[13px]">
                                    <span className="text-[color:var(--bm-text-muted)]">{r.label}</span>
                                    <span className={`font-semibold ${r.cls}`}>{r.val}</span>
                                </div>
                            ))}
                        </div>
                        {proposals.won + proposals.lost > 0 && (
                            <div className="pt-4 border-t border-[color:var(--bm-border)]">
                                <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-2">Win rate</div>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 h-2 rounded-full bg-[color:var(--bm-bg-mute)] overflow-hidden">
                                        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${proposals.win_rate}%` }} />
                                    </div>
                                    <span className="text-[13px] font-semibold text-emerald-600">{proposals.win_rate}%</span>
                                </div>
                            </div>
                        )}
                        {can('proposals', 'create') && (
                            <button onClick={() => navigate('/proposals?new=1')}
                                className="mt-4 w-full h-9 rounded-md text-[12.5px] font-medium border border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] transition text-[color:var(--bm-text-muted)]">
                                + New proposal
                            </button>
                        )}
                    </div>
                </div>

                {/* ── Renewals + Activity ── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
                        <div className="flex items-center gap-2 text-[13.5px] font-semibold text-[color:var(--bm-text)] mb-5">
                            <CalendarClock size={16} className="text-[color:var(--bm-primary)]" /> Upcoming renewals
                        </div>
                        <div className="space-y-3">
                            {[
                                { label: 'Next 30 days', value: renewals.d30 },
                                { label: 'Next 60 days', value: renewals.d60 },
                                { label: 'Next 90 days', value: renewals.d90 },
                            ].map(r => (
                                <div key={r.label} className="flex items-center justify-between py-2 border-b border-[color:var(--bm-border)] last:border-0">
                                    <span className="text-[13px] text-[color:var(--bm-text-muted)]">{r.label}</span>
                                    <span className="font-display text-[17px] font-semibold text-[color:var(--bm-text)]">{r.value}</span>
                                </div>
                            ))}
                        </div>
                        {revenue.renewal_mrr_at_risk > 0 && (
                            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-[12.5px]">
                                <span className="font-semibold text-amber-800">{fmt(revenue.renewal_mrr_at_risk)}/mo</span>
                                <span className="text-amber-700"> MRR up for renewal in 90d</span>
                            </div>
                        )}
                        <button onClick={() => navigate('/renewals')}
                            className="mt-3 w-full h-8 rounded-md text-[12px] font-medium border border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] transition text-[color:var(--bm-text-muted)]">
                            View all renewals
                        </button>
                    </div>

                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6 lg:col-span-2">
                        <div className="flex items-center gap-2 text-[13.5px] font-semibold text-[color:var(--bm-text)] mb-5">
                            <Sparkles size={16} className="text-[color:var(--bm-accent-600)]" /> Recent activity
                        </div>
                        {recent_activity.length === 0 ? (
                            <div className="text-[13px] text-[color:var(--bm-text-muted)] py-6 text-center">No activity yet.</div>
                        ) : (
                            <div className="space-y-0">
                                {recent_activity.map(a => (
                                    <div key={a.id} className="flex items-start gap-3 text-[12.5px] py-2 border-b border-[color:var(--bm-border)] last:border-0">
                                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[color:var(--bm-accent)] shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <span className="font-medium text-[color:var(--bm-text)]">{a.actor_name || 'System'}</span>
                                            {' '}<span className="text-[color:var(--bm-text-muted)]">{a.action} {a.entity_type}</span>
                                        </div>
                                        <span className="text-[11.5px] text-[color:var(--bm-text-subtle)] shrink-0">{new Date(a.timestamp).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── LeadGen tile ── */}
                {leadgen && (
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6" data-testid="leadgen-tile">
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-2 text-[13.5px] font-semibold text-[color:var(--bm-text)]">
                                <Target size={16} className="text-[color:var(--bm-primary)]" /> Lead Gen
                            </div>
                            <button onClick={() => navigate('/leadgen/pipeline')} className="text-[12px] text-[color:var(--bm-primary)] hover:underline">Open pipeline</button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                            <div onClick={() => navigate('/leadgen/leads')} className="bg-[color:var(--bm-bg-mute)] rounded-lg px-3 py-2.5 border border-transparent hover:border-[color:var(--bm-border)] transition cursor-pointer" data-testid="lg-total-leads">
                                <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-medium">Total leads</div>
                                <div className="font-display text-[20px] font-semibold text-[color:var(--bm-text)] mt-0.5">{leadgen.total_leads}</div>
                            </div>
                            <div className="bg-emerald-50 rounded-lg px-3 py-2.5 border border-emerald-100" data-testid="lg-won-month">
                                <div className="text-[10.5px] uppercase tracking-wide text-emerald-700 font-medium">Won this month</div>
                                <div className="font-display text-[20px] font-semibold text-emerald-700 mt-0.5">{leadgen.won_this_month}</div>
                            </div>
                            <div
                                onClick={() => navigate('/leadgen/leads?follow_up=due')}
                                className={`rounded-lg px-3 py-2.5 border transition cursor-pointer ${
                                    (leadgen.follow_ups_overdue || 0) > 0
                                        ? 'bg-amber-50 border-amber-200 hover:border-amber-400'
                                        : (leadgen.follow_ups_due || 0) > 0
                                            ? 'bg-sky-50 border-sky-200 hover:border-sky-400'
                                            : 'bg-[color:var(--bm-bg-mute)] border-transparent hover:border-[color:var(--bm-border)]'
                                }`}
                                data-testid="lg-follow-ups"
                            >
                                <div className={`text-[10.5px] uppercase tracking-wide font-medium ${
                                    (leadgen.follow_ups_overdue || 0) > 0 ? 'text-amber-800'
                                        : (leadgen.follow_ups_due || 0) > 0 ? 'text-sky-700'
                                            : 'text-[color:var(--bm-text-subtle)]'
                                }`}>Follow-ups due</div>
                                <div className={`font-display text-[20px] font-semibold mt-0.5 ${
                                    (leadgen.follow_ups_overdue || 0) > 0 ? 'text-amber-800'
                                        : (leadgen.follow_ups_due || 0) > 0 ? 'text-sky-700'
                                            : 'text-[color:var(--bm-text)]'
                                }`}>
                                    {leadgen.follow_ups_due || 0}
                                </div>
                                {(leadgen.follow_ups_overdue || 0) > 0 && (
                                    <div className="text-[10.5px] text-amber-700 mt-0.5 font-medium">
                                        {leadgen.follow_ups_overdue} overdue
                                    </div>
                                )}
                            </div>
                            <div onClick={() => navigate('/leadgen/campaigns')} className="bg-[color:var(--bm-bg-mute)] rounded-lg px-3 py-2.5 border border-transparent hover:border-[color:var(--bm-border)] transition cursor-pointer" data-testid="lg-campaigns">
                                <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-medium">Campaigns sent</div>
                                <div className="font-display text-[20px] font-semibold text-[color:var(--bm-text)] mt-0.5">{leadgen.active_campaigns}</div>
                            </div>
                            <div className="bg-[color:var(--bm-bg-mute)] rounded-lg px-3 py-2.5 border border-transparent" data-testid="lg-spend">
                                <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-medium">API spend</div>
                                <div className="font-display text-[20px] font-semibold text-[color:var(--bm-text)] mt-0.5">${leadgen.total_cost?.toFixed(2) ?? '0.00'}</div>
                            </div>
                        </div>
                        {leadgen.total_leads > 0 && (
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-2">Pipeline distribution</div>
                                <div className="flex h-2 rounded-full overflow-hidden bg-[color:var(--bm-bg-mute)]">
                                    {[
                                        ['new_leads', '#94a3b8'],
                                        ['verified',  '#6366F1'],
                                        ['contacted', '#F59E0B'],
                                        ['engaged',   '#10B981'],
                                        ['qualified', '#00A86B'],
                                        ['won',       '#059669'],
                                        ['lost',      '#ef4444'],
                                    ].map(([key, color]) => {
                                        const n = leadgen.stage_counts?.[key] || 0;
                                        const pct = leadgen.total_leads ? (n / leadgen.total_leads) * 100 : 0;
                                        if (pct === 0) return null;
                                        return <div key={key} title={`${key}: ${n}`} style={{ width: `${pct}%`, background: color }} />;
                                    })}
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-[color:var(--bm-text-muted)]">
                                    {Object.entries(leadgen.stage_counts || {}).filter(([_, n]) => n > 0).map(([key, n]) => (
                                        <span key={key}>{key.replace(/_/g, ' ')}: <span className="font-semibold text-[color:var(--bm-text)]">{n}</span></span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
